import {
  computed,
  type Ref,
} from 'vue'
import useContextStore from 'src/store/useContextStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type {
  AppointmentPlaceType,
  LpuDoctor,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'
import getLpuPhone from 'src/utils/getLpuPhone'
import formatPhone from 'src/functions/formatPhone'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getDoctorPlaceTypes from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorPlaceTypes'

type UseDoctorCardVisibilityOptions = {
  doctor: LpuDoctor
  lpu: Lpu
  isMobile: Ref<boolean>
  placeType: Ref<AppointmentPlaceType>
  isSlotsLoading: Ref<boolean>
  hasBookableSlots: Ref<boolean>
  isTelemedLoading: Ref<boolean>
}

/**
 * Флаги блоков карточки врача: цены, запись, звонок, правая колонка.
 *
 * Слоты календаря сюда не входят — их считает `useDoctorCardSchedule`.
 */
export default function useDoctorCardVisibility({
  doctor,
  lpu,
  isMobile,
  placeType,
  isSlotsLoading,
  hasBookableSlots,
  isTelemedLoading,
}: UseDoctorCardVisibilityOptions) {
  const { context }: ContextStore = useContextStore()
  const isStaff = computed(() => Boolean(context.request.user.is_staff))
  const appointment = computed(() => doctor.workplace_info?.appointment || {})
  const staffAppointmentType = computed(() => doctor.workplace_info?.staff_appointment_type || '')
  const isExpressAppointment = computed(() => (
    doctor.workplace_info?.appointment_type === 'express'
  ))
  const isExpressClosed = computed(() => (
    isExpressAppointment.value && Boolean(context.nearest_appointment_express_datetime)
  ))
  const showAppointmentBlock = computed(() => (
    Boolean(appointment.value.is_on || appointment.value.time)
  ))
  const showPriceBlock = computed(() => (
    Boolean(appointment.value.is_on && getDoctorPlaceTypes(doctor.min_prices).length)
  ))
  const loyaltyProgram = computed(() => (
    lpu.closed ? null : lpu.lpu_loyalty_program
  ))
  const clinicLoyaltyProgram = computed(() => (
    placeType.value === 'clinic' ? loyaltyProgram.value : null
  ))
  const lpuPhoneRaw = computed(() => getLpuPhone({
    lpu,
    lpuAddress: lpu.addr?.[0],
  }) || lpu.lpuphone?.phone || '')
  const lpuPhoneHref = computed(() => {
    if (!lpuPhoneRaw.value) {
      return ''
    }

    return formatPhone({
      phone: lpuPhoneRaw.value,
      country: { iso: context.request.country.iso.toLowerCase() },
      isRaw: true,
    })
  })
  const showHomeCallButton = computed(() => (
    isMobile.value
    && Boolean(lpu.appointment_on && lpuPhoneHref.value && !isExpressClosed.value)
  ))
  const showClinicCallButton = computed(() => (
    isMobile.value
    && placeType.value === 'clinic'
    && isExpressAppointment.value
    && !isExpressClosed.value
    && Boolean(lpu.appointment_on)
    && Boolean(lpu.appointment_phone)
    && Boolean(lpu.lpuphone?.active)
    && Boolean(lpuPhoneHref.value)
  ))
  const homeCallLabel = computed(() => (
    doctor.is_medic === false
      ? `${tStatic('Вызвать')} ${tStatic('специалиста')}`
      : `${tStatic('Вызвать')} ${tStatic('врача')}`
  ))
  const expectsClinicSlots = computed(() => (
    placeType.value === 'clinic'
    && Boolean(appointment.value.has_slots && appointment.value.is_on)
  ))
  const isCardSlotsLoading = computed(() => (
    (expectsClinicSlots.value && isSlotsLoading.value)
    || isTelemedLoading.value
  ))
  const showCalendar = computed(() => (
    placeType.value !== 'home'
    && hasBookableSlots.value
  ))
  const showAppointmentStub = computed(() => (
    placeType.value !== 'home'
    && !isCardSlotsLoading.value
    && !hasBookableSlots.value
    && (
      expectsClinicSlots.value
      || placeType.value === 'telemed'
    )
  ))
  const hasAppointmentContent = computed(() => {
    if (!showAppointmentBlock.value) {
      return false
    }

    return Boolean(
      isExpressClosed.value
      || showCalendar.value
      || showAppointmentStub.value
      || isCardSlotsLoading.value
      || placeType.value === 'home'
      || showClinicCallButton.value,
    )
  })
  const hasStaffScheduleMeta = computed(() => (
    isStaff.value
    && !isMobile.value
    && Boolean(
      doctor.last_relevance
      || (showAppointmentBlock.value && staffAppointmentType.value),
    )
  ))
  const showRightColumn = computed(() => (
    hasAppointmentContent.value
    || hasStaffScheduleMeta.value
    || Boolean(doctor.has_restriction_appointment)
  ))

  return {
    isStaff,
    staffAppointmentType,
    isExpressAppointment,
    isExpressClosed,
    showAppointmentBlock,
    showPriceBlock,
    clinicLoyaltyProgram,
    lpuPhoneHref,
    showHomeCallButton,
    showClinicCallButton,
    homeCallLabel,
    isCardSlotsLoading,
    showCalendar,
    showAppointmentStub,
    showRightColumn,
  }
}
